import { type Dispatch } from 'store';
import { type GlobalState } from 'store/reducer';
import { type Action, STATUS_SUCCESS, clearCacheAction, requestsSelector } from '@lingoda/core';
import { getApp } from '@lingoda/env';
import { fetchStudentModules } from '@lingoda/modules';
import { showNotifications } from '@lingoda/notifications';
import { studentIdSelector } from '@lingoda/students';
import { fetchSubscriptions } from '@lingoda/subscriptions';
import type { Dependency, Notification, NotificationHandler } from './_types';

let handlers: NotificationHandler[] = [];

export const addNotificationHandler = (handler: NotificationHandler) => {
    if (!handler.afterLogin) {
        handlers.push(handler);
    }
};

export const getNotificationHandlers = () => handlers;

const dependencyResolver = {
    subscriptions: fetchSubscriptions,
    modules: fetchStudentModules,
};

const actions: { [key: string]: { meta: { id: string } } } = {};

const initDependencies = (dispatch: Dispatch) => {
    Object.entries(dependencyResolver).map(([key, action]) => {
        actions[key] = dispatch(action());
    });
};

let cachedStudentId: number | undefined;

type Requests = ReturnType<typeof requestsSelector>;
const areDependenciesFulfilled = (dependencies: Dependency[], requests: Requests) =>
    !dependencies ||
    dependencies.every((dependency) => {
        const request = requests[actions[dependency] && actions[dependency].meta.id];

        return request && request.status === STATUS_SUCCESS;
    });

const filterNotifications = (result: Notification | Notification[]) =>
    (Array.isArray(result) ? result : [result]).filter((value) => !!value);

// Skip actions that don't change the store themselves in meaningful way
// (like, requests status updates etc)
// request/REQUEST_SUCCESS are used to fullfill deps
export const isActionTypeSkippable = (actionType: string) =>
    !!(actionType.match(/^request.(?!REQUEST_SUCCESS$)/) || actionType.match(/SET_TIME/));

export default (action: Action<unknown>, state: GlobalState, dispatch: Dispatch) => {
    const studentId = studentIdSelector(state);
    const requests = requestsSelector(state);

    if (cachedStudentId !== studentId) {
        cachedStudentId = studentId;
        initDependencies(dispatch);
    }

    if (action.type === clearCacheAction.toString()) {
        initDependencies(dispatch);
        handlers.filter((handler) => handler.clean && handler.clean());

        return;
    }

    if (isActionTypeSkippable(action.type)) {
        return;
    }

    let pendingNotifications: Notification[] = [];

    handlers = handlers.filter((handler) => {
        if (handler.dependencies && !areDependenciesFulfilled(handler.dependencies, requests)) {
            return true;
        }

        const results = handler.check(state, action);
        if (!results || (Array.isArray(results) && !results.length)) {
            return true;
        }

        if (handler.platforms && !handler.platforms.includes(getApp())) {
            return true;
        }

        pendingNotifications = [...pendingNotifications, ...filterNotifications(results)];

        return !handler.once;
    });

    if (pendingNotifications.length) {
        dispatch(showNotifications(pendingNotifications));
    }
};
